// src/components/mdx/client/TableComponent.client.tsx

'use client'

/**
 * @module components/mdx/client/TableComponent.client
 * @description Client implementations for tables (search/sort/pagination/motion). Loaded via Suspense from server entry.
 */

import { type ReactNode, useState, useMemo, type ChangeEvent } from 'react'
import { motion } from "motion/react"
import { cn } from '~/lib/utils'
import { safeString } from '~/lib/utils/formatters'
import RiArrowDownSLine from '~/icons/remix/generated/RiArrowDownSLine'
import RiArrowUpSLine from '~/icons/remix/generated/RiArrowUpSLine'
import RiSearchLine from '~/icons/remix/generated/RiSearchLine'
import RiFilterLine from '~/icons/remix/generated/RiFilterLine'
import { createSafeMDXComponent } from '~/components/mdx/utils/component-utils.client'

/** Column definition (see server types for full docs). */
export interface TableColumn {
  header: string
  accessorKey: string
  className?: string
  maxWidth?: string
  sortable?: boolean
  filterable?: boolean
  renderCell?: (value: unknown) => ReactNode
}

/** Basic table props (see server types for full docs). */
export interface TableProps {
  columns: TableColumn[]
  data: Record<string, unknown>[]
  className?: string
}

/** Enhanced table props (see server types for full docs). */
export interface EnhancedTableProps extends TableProps {
  caption?: string
  footer?: ReactNode
  striped?: boolean
  hoverable?: boolean
  searchable?: boolean
  filterable?: boolean
  sortable?: boolean
  pagination?: boolean
  pageSize?: number
  stickyHeader?: boolean
  theme?: 'default' | 'minimal' | 'modern'
  density?: 'compact' | 'normal' | 'relaxed'
}

/** Format a value for display; delegates to custom cell render if provided. */
function formatCellValue(value: unknown, render?: (value: unknown) => ReactNode): ReactNode {
  if (render) return render(value)
  return safeString(value, {
    fallback: '',
    objectFallback: '[Object]',
    arrayFallback: '[Array]',
    tryJSON: true,
    maxLength: 200,
    trimWhitespace: true,
  })
}

/**
 * EnhancedTableComponentClient
 * Client-side enhanced table with search, sorting, pagination, theming, density, and subtle motion.
 */
export function EnhancedTableComponentClient({
  columns,
  data,
  caption,
  footer,
  striped = false,
  hoverable = true,
  searchable = false,
  filterable = false,
  sortable = true,
  pagination = false,
  pageSize = 10,
  stickyHeader = false,
  theme = 'modern',
  density = 'normal',
  className,
}: EnhancedTableProps): JSX.Element {
  const [searchQuery, setSearchQuery] = useState('')
  const [sortConfig, setSortConfig] = useState<{ key: string | null; direction: 'asc' | 'desc' }>({
    key: null,
    direction: 'asc',
  })
  const [currentPage, setCurrentPage] = useState(1)

  const filteredData = useMemo(() => {
    if (!searchQuery) return data
    const query = searchQuery.toLowerCase()
    return data.filter((row) =>
      Object.values(row).some((value) => {
        const stringValue = safeString(value, {
          fallback: '',
          tryJSON: true,
          preserveNumbers: true,
          preserveBooleans: true,
        })
        return stringValue.toLowerCase().includes(query)
      })
    )
  }, [data, searchQuery])

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData
    return [...filteredData].sort((a, b) => {
      const sortKey = sortConfig.key
      if (!sortKey) return 0
      const aValue = a[sortKey]
      const bValue = b[sortKey]
      if (aValue === bValue) return 0
      if (aValue == null) return 1
      if (bValue == null) return -1
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return aValue.localeCompare(bValue) * (sortConfig.direction === 'asc' ? 1 : -1)
      }
      return (aValue < bValue ? -1 : 1) * (sortConfig.direction === 'asc' ? 1 : -1)
    })
  }, [filteredData, sortConfig])

  const paginatedData = useMemo(() => {
    if (!pagination) return sortedData
    const start = (currentPage - 1) * pageSize
    return sortedData.slice(start, start + pageSize)
  }, [sortedData, currentPage, pageSize, pagination])

  const totalPages = Math.ceil(sortedData.length / pageSize)

  const themeStyles = {
    default: {
      wrapper: 'bg-white dark:bg-gray-900',
      table: 'min-w-full divide-y divide-gray-200 dark:divide-gray-700',
      header: 'bg-gray-100 dark:bg-gray-800',
      headerCell: 'px-4 py-3 text-left text-gray-900 dark:text-gray-100 font-medium',
      row: 'bg-white dark:bg-gray-900',
      cell: 'px-4 py-3 text-gray-700 dark:text-gray-300',
      footer: 'bg-gray-50 dark:bg-gray-800',
      caption:
        'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 font-semibold text-lg px-4 py-2',
    },
    minimal: {
      wrapper: 'bg-transparent',
      table: 'min-w-full',
      header: 'border-b border-gray-200 dark:border-gray-800',
      headerCell: 'px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium',
      row: 'border-b border-gray-100 dark:border-gray-800/60',
      cell: 'px-3 py-2 text-gray-600 dark:text-gray-400',
      footer: 'border-t border-gray-200 dark:border-gray-800',
      caption: 'text-gray-700 dark:text-gray-300 font-medium px-3 py-2',
    },
    modern: {
      wrapper:
        'bg-gradient-to-br from-white via-gray-50/50 to-white dark:from-gray-900 dark:via-gray-800/50 dark:to-gray-900',
      table: 'min-w-full divide-y divide-gray-200/50 dark:divide-gray-800/50',
      header:
        'bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 shadow-sm backdrop-blur-sm',
      headerCell:
        'px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white tracking-wider',
      row: 'backdrop-blur-sm bg-white/80 dark:bg-gray-900/80 transition-colors duration-200',
      cell: 'px-6 py-4 text-sm text-gray-600 dark:text-gray-300',
      footer:
        'bg-gradient-to-t from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 shadow-sm backdrop-blur-sm',
      caption:
        'bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 text-gray-900 dark:text-white font-semibold text-xl px-6 py-4',
    },
  }[theme]

  const densityStyles = {
    compact: { headerCell: 'px-3 py-2', cell: 'px-3 py-2' },
    normal: { headerCell: 'px-4 py-3', cell: 'px-4 py-3' },
    relaxed: { headerCell: 'px-6 py-4', cell: 'px-6 py-4' },
  }[density]

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={cn(
        'overflow-hidden rounded-lg border border-gray-200 dark:border-gray-800 shadow-sm',
        'transition duration-200 ease-in-out',
        themeStyles.wrapper,
        className
      )}
    >
      {(searchable || filterable) && (
        <div className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            {searchable && (
              <div className="relative flex-1">
                <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
                <input
                  type="search"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setCurrentPage(1)
                    setSearchQuery(e.target.value)
                  }}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 py-2 pl-10 pr-3 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 transition duration-200"
                />
              </div>
            )}
            {filterable && (
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-md bg-white dark:bg-gray-800 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition duration-200"
              >
                <RiFilterLine className="h-4 w-4" />
                Filter
              </button>
            )}
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className={cn(themeStyles.table)}>
          {caption && <caption className={cn(themeStyles.caption)}>{caption}</caption>}
          <thead className={cn(themeStyles.header, stickyHeader && 'sticky top-0 z-10')}>
            <tr>
              {columns.map((column, index) => {
                const isSortable = sortable && column.sortable !== false
                const isActive = sortConfig.key === column.accessorKey
                return (
                  <th
                    key={index}
                    scope="col"
                    role={isSortable ? 'button' : undefined}
                    tabIndex={isSortable ? 0 : undefined}
                    className={cn(
                      themeStyles.headerCell,
                      densityStyles.headerCell,
                      'break-words',
                      column.className,
                      isSortable &&
                        'cursor-pointer select-none hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200'
                    )}
                    style={column.maxWidth ? { maxWidth: column.maxWidth } : undefined}
                    onClick={() => {
                      if (!isSortable) return
                      setCurrentPage(1)
                      setSortConfig((prev) => ({
                        key: column.accessorKey,
                        direction:
                          prev.key === column.accessorKey && prev.direction === 'asc'
                            ? 'desc'
                            : 'asc',
                      }))
                    }}
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter' || !isSortable) return
                      setCurrentPage(1)
                      setSortConfig((prev) => ({
                        key: column.accessorKey,
                        direction:
                          prev.key === column.accessorKey && prev.direction === 'asc'
                            ? 'desc'
                            : 'asc',
                      }))
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <span>{column.header}</span>
                      {isSortable && isActive && (
                        sortConfig.direction === 'asc' ? (
                          <RiArrowUpSLine className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                        ) : (
                          <RiArrowDownSLine className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                        )
                      )}
                    </div>
                  </th>
                )
              })}
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
            {paginatedData.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className={cn(
                  themeStyles.row,
                  striped && rowIndex % 2 === 0 && 'bg-gray-50 dark:bg-gray-800/50',
                  hoverable && 'hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors duration-200'
                )}
              >
                {columns.map((column, colIndex) => (
                  <td
                    key={colIndex}
                    className={cn(themeStyles.cell, densityStyles.cell, 'break-words', column.className)}
                    style={column.maxWidth ? { maxWidth: column.maxWidth } : undefined}
                  >
                    {formatCellValue(row[column.accessorKey], column.renderCell)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>

          {(footer ?? pagination) && (
            <tfoot className={themeStyles.footer}>
              {footer && (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                    {footer}
                  </td>
                </tr>
              )}
              {pagination && totalPages > 1 && (
                <tr>
                  <td colSpan={columns.length}>
                    <div className="flex items-center justify-between px-4 py-3 sm:px-6">
                      <div className="flex flex-1 justify-between sm:hidden">
                        <button
                          onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                          disabled={currentPage === totalPages}
                          className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
                        >
                          Next
                        </button>
                      </div>

                      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                        <div>
                          <p className="text-sm text-gray-700 dark:text-gray-300">
                            Showing{' '}
                            <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
                            <span className="font-medium">
                              {Math.min(currentPage * pageSize, sortedData.length)}
                            </span>{' '}
                            of <span className="font-medium">{sortedData.length}</span> results
                          </p>
                        </div>
                        <div>
                          <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                            <button
                              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                              disabled={currentPage === 1}
                              className="relative inline-flex items-center rounded-l-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
                              aria-label="Previous"
                            >
                              <span className="sr-only">Previous</span>
                              <RiArrowDownSLine className="h-5 w-5 rotate-90" />
                            </button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                              <button
                                key={page}
                                onClick={() => setCurrentPage(page)}
                                className={cn(
                                  'relative inline-flex items-center border px-4 py-2 text-sm font-medium transition duration-200',
                                  page === currentPage
                                    ? 'z-10 bg-gray-50 dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300'
                                    : 'bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                                )}
                              >
                                {page}
                              </button>
                            ))}
                            <button
                              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                              disabled={currentPage === totalPages}
                              className="relative inline-flex items-center rounded-r-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
                              aria-label="Next"
                            >
                              <span className="sr-only">Next</span>
                              <RiArrowDownSLine className="h-5 w-5 -rotate-90" />
                            </button>
                          </nav>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tfoot>
          )}
        </table>
      </div>
    </motion.div>
  )
}

/**
 * TableComponentClient
 * Simplified, backward-compatible wrapper over the enhanced table with opinionated defaults.
 */
export function TableComponentClient({ columns, data, className }: TableProps): JSX.Element {
  return (
    <EnhancedTableComponentClient
      columns={columns}
      data={data}
      className={className}
      theme="default"
      hoverable={false}
      striped={false}
    />
  )
}

// ===== SAFE EXPORTS FOR SERVER COMPONENTS =====

/**
 * Safe version of EnhancedTableComponentClient with error boundaries and loading states.
 * This is the version that should be imported by server components.
 */
export const SafeEnhancedTableComponentClient = createSafeMDXComponent(
  () => Promise.resolve({ default: EnhancedTableComponentClient }),
  {
    componentName: 'EnhancedTableComponentClient',
    category: 'table' as const,
    ssr: false,
    isHierarchical: false,
    preserveChildrenOnError: false,
    disableDefaultWrapper: true // Already wrapped by the component itself
  }
)

/**
 * Safe version of TableComponentClient with error boundaries and loading states.
 * This is the version that should be imported by server components.
 */
export const SafeTableComponentClient = createSafeMDXComponent(
  () => Promise.resolve({ default: TableComponentClient }),
  {
    componentName: 'TableComponentClient',
    category: 'table' as const,
    ssr: false,
    isHierarchical: false,
    preserveChildrenOnError: false,
    disableDefaultWrapper: true // Already wrapped by the component itself
  }
)
